<template>
  <div class="p-6">
    <h1 class="text-2xl font-bold mb-6">Riepilogo Configurazioni Clienti</h1>

    <div class="mb-4 flex items-center gap-3 flex-wrap">
      <input
        v-model="search"
        type="text"
        placeholder="🔎︎ Cerca cliente..."
        class="p93__input_general px-4 py-2 min-w-[240px]"
      />
      <select
        v-model="selectedModule"
        class="p93__input_general pl-4 pr-7 py-2"
      >
        <option value="">Tutti i moduli</option>
        <option v-for="mod in availableModules" :key="mod" :value="mod">{{ mod }}</option>
      </select>
      <select
        v-if="selectedModule"
        v-model="selectedSubmodule"
        class="p93__input_general pl-4 pr-7 py-2"
      >
        <option value="">Tutti i sottomoduli</option>
        <option v-for="submodule in availableSubmodules" :key="submodule" :value="submodule">
          {{ $t(submodule) }}
        </option>
      </select>
    </div>

    <div v-if="filteredCustomers.length === 0" class="text-gray-500 py-8 text-center">
      Nessun cliente trovato.
    </div>

    <v-expansion-panels v-else variant="accordion" multiple v-model="openPanels">
      <v-expansion-panel
        v-for="customer in filteredCustomers"
        :key="customer.id"
        class="!rounded-lg border border-gray-300 !shadow-sm"
      >
        <v-expansion-panel-title>
          <div class="flex items-center gap-4 w-full min-w-0">
            <span class="font-semibold text-base flex-shrink-0">{{ customer.name }}</span>
            <span class="text-gray-400 text-sm truncate hidden sm:block">{{ customer.email }}</span>
            <v-spacer />
            <span class="text-sm text-gray-600 flex-shrink-0">
              {{ customer.users_count }} utenti &nbsp;/&nbsp; {{ customer.max_sessions ?? '—' }} lim. sessioni - {{ customer.modules.length }} moduli
            </span>
            <button
              v-if="customer.id !== $page.props.customer?.id"
              @click.stop="switchToCustomer(customer.id)"
              @mousedown.stop
              class="relative z-10 flex-shrink-0 text-xs px-3 py-1 rounded bg-gray-200 hover:bg-red-500 hover:text-white transition-colors"
              title="Cambia azienda e vai alla dashboard"
            >Cambia azienda</button>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">

            <div class="bg-gray-50 rounded-lg p-4">
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Informazioni generali</h3>
              <div class="space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Email</span>
                  <span class="font-medium">{{ customer.email }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Limite sessioni concorrenti</span>
                  <span class="font-medium">{{ customer.max_sessions ?? '—' }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Google Maps Key</span>
                  <span class="font-medium font-mono text-xs" :title="customer.maps_key">
                    {{ customer.maps_key || '—' }}
                  </span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Bridge URL</span>
                  <span v-if="customer.bridge_url" class="font-medium font-mono text-xs" :title="customer.bridge_url">
                    {{ customer.bridge_url }}
                  </span>
                  <span v-else class="font-medium">—</span>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 rounded-lg p-4">
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Utenti ({{ customer.users.length }})
              </h3>
              <div v-if="customer.users.length === 0" class="text-gray-400 text-sm">Nessun utente</div>
              <div v-else class="columns-2 gap-2 max-h-48 overflow-y-auto pr-1">
                <div
                  v-for="user in customer.users"
                  :key="user.email"
                  class="flex items-center gap-2 text-sm break-inside-avoid mb-1"
                >
                  <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex-shrink-0">
                    {{ userInitials(user.name) }}
                  </span>
                  <div class="min-w-0 flex-1">
                    <div class="font-medium truncate">{{ user.name }}</div>
                    <div class="text-gray-400 text-xs truncate">{{ user.email }}</div>
                    <div v-if="user.roles && user.roles.length" class="flex flex-wrap gap-1 mt-0.5">
                      <span
                        v-for="role in user.roles"
                        :key="role"
                        class="inline-block text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium leading-none"
                      >{{ role }}</span>
                    </div>
                    <div v-if="user.last_activity" class="text-blue-500 text-xs">{{ user.last_activity }}</div>
                    <div v-else class="text-gray-400 text-xs">Nessun accesso nelle ultime 48h</div>
                  </div>
                  <button
                    @click="loginAs(user.id)"
                    class="flex-shrink-0 text-xs px-2 py-0.5 rounded bg-gray-200 hover:bg-red-500 hover:text-white transition-colors"
                    title="Accedi come questo utente"
                  >Accedi</button>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 rounded-lg p-4 md:col-span-2">
              <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Moduli attivi ({{ customer.modules.length }})
              </h3>
              <div v-if="customer.modules.length === 0" class="text-gray-400 text-sm">Nessun modulo attivo</div>
              <div v-else class="flex flex-wrap gap-3">
                <div
                  v-for="module in customer.modules"
                  :key="module.slug"
                  class="border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-[140px]"
                >
                  <div class="font-medium text-sm text-gray-800 mb-1">{{ module.name }}</div>
                  <ul v-if="module.active_submodules.length > 0" class="list-disc list-inside mt-1 space-y-0.5">
                    <li
                      v-for="sub in module.active_submodules"
                      :key="sub"
                      class="text-xs text-gray-700"
                    >
                      {{ $t(sub) }}
                    </li>
                  </ul>
                  <div v-else class="text-xs text-gray-400 mt-1">Nessun sottomodulo</div>
                </div>
              </div>
            </div>

          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import Layout from '@/Layouts/Layout.vue';
import { router } from '@inertiajs/vue3';
import { VExpansionPanels, VExpansionPanel, VExpansionPanelTitle, VExpansionPanelText, VSpacer, VChip } from 'vuetify/components';

export default {
  layout: Layout,
  components: {
    VExpansionPanels,
    VExpansionPanel,
    VExpansionPanelTitle,
    VExpansionPanelText,
    VSpacer,
    VChip,
  },
  props: {
    customers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      selectedModule: '',
      selectedSubmodule: '',
      openPanels: [],
    };
  },
  watch: {
    selectedModule() {
      this.selectedSubmodule = '';
    },
  },
  computed: {
    availableModules() {
      const names = new Set();
      this.customers.forEach((c) => c.modules.forEach((m) => names.add(m.name)));
      return Array.from(names).sort();
    },
    availableSubmodules() {
      const submodules = new Set();
      this.customers.forEach((customer) => {
        const module = customer.modules.find((item) => item.name === this.selectedModule);
        (module?.active_submodules || []).forEach((submodule) => submodules.add(submodule));
      });
      return Array.from(submodules).sort();
    },
    filteredCustomers() {
      return this.customers.filter((c) => {
        const matchesSearch =
          !this.search ||
          c.name.toLowerCase().includes(this.search.toLowerCase()) ||
          (c.email && c.email.toLowerCase().includes(this.search.toLowerCase()));
        const selectedModule = c.modules.find((m) => m.name === this.selectedModule);
        const matchesModule = !this.selectedModule || Boolean(selectedModule);
        const matchesSubmodule =
          !this.selectedSubmodule ||
          (selectedModule?.active_submodules || []).includes(this.selectedSubmodule);
        return matchesSearch && matchesModule && matchesSubmodule;
      });
    },
  },
  methods: {
    userInitials(name) {
      const parts = name.split(' ');
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    },
    loginAs(userId) {
      if (!confirm('Accedere come questo utente? Dovrai fare logout per tornare al tuo account.')) return;
      router.post(route('core.login-as', { userId }));
    },
    switchToCustomer(customerId) {
      router.post(route('customer-choice.choose'), { customer_id: customerId });
    },
  },
};
</script>
